import React, { useState, useEffect, useRef } from 'react';

import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { FaBars } from 'react-icons/fa';


import { buildMediaUrl } from '../config/api';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const { openLogin, openSignup } = useModal();
  const { user, logout } = useAuth();

  // Debug: Log user data to console
  useEffect(() => {
    console.log('Navbar - User data:', user);
    console.log('Navbar - profilePhoto:', user?.profilePhoto);
  }, [user]);


  // Close mobile menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!isMenuOpen) return;
      const clickedToggle = toggleRef.current && toggleRef.current.contains(e.target);
      const clickedMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!clickedToggle && !clickedMenu) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMenuOpen]);

  return (
    <nav className="fixed w-full top-0 z-50 bg-black shadow-[0_10px_30px_rgba(0,0,0,0.5)] border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          
          {/* Logo and Brand Name */}
          <div className="flex items-center space-x-3 group cursor-pointer">
            <div className="flex items-center justify-center w-12 h-12 bg-white/10 backdrop-blur-md rounded-full shadow-md border border-white/10 group-hover:scale-105 group-hover:shadow-pink-500/20 group-hover:border-pink-500/30 transition-all duration-300">
              <img 
                src="/images/logo.png" 
                alt="MarryCenter Logo"
                className="w-8 h-8 rounded-full object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-pink-500 group-hover:text-pink-400 transition-colors" style={{fontFamily: 'Helvetica, Arial, sans-serif'}}>
                MarryCenter
              </span>
              <span className="text-xs text-white font-medium tracking-wide -mt-1">Find Your Perfect Match</span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-white hover:text-pink-400 transition-colors duration-200 font-medium">
              Features
            </a>
            <a href="#success" className="text-white hover:text-pink-400 transition-colors duration-200 font-medium">
              Success Stories
            </a>
          </div>

          {/* Auth Buttons / Profile - Desktop */}
          <div className="hidden md:flex items-center space-x-4">

            {user ? (
              /* User Profile Section */
              <div className="flex items-center space-x-3">
                {/* User Profile Picture */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white font-semibold shadow-lg hover:shadow-pink-500/30 transition-shadow duration-200 cursor-pointer border-2 border-white dark:border-slate-800">
                    {user?.profilePhoto ? (
                      <img 
                        src={buildMediaUrl(user.profilePhoto)} 
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-full h-full rounded-full object-cover"
                        onLoad={() => console.log('✅ Navbar profile image loaded successfully')}
                        onError={(e) => {
                          console.error('❌ Navbar profile image failed to load:', buildMediaUrl(user.profilePhoto));
                          console.error('❌ Error details:', e);
                        }}
                      />
                    ) : (
                      <span className="text-white font-semibold">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{user?.membershipType === 'premium' ? 'Premium Member' : 'Free Member'}</p>
                  </div>
                </div>

                {/* Logout Button */}
                <button 
                  onClick={logout}
                  className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 border border-slate-300 dark:border-slate-700 rounded-full font-medium hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all duration-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              /* Login/Signup Buttons */
              <>
                <button 
                  onClick={() => openLogin()}
                  className="px-6 py-2 text-pink-400 hover:text-pink-300 font-semibold hover:bg-white/5 rounded-full transition-all duration-200"
                >
                  Login
                </button>
                <button 
                  onClick={() => openSignup()}
                  className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-semibold hover:from-pink-600 hover:to-rose-600 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(236,72,153,0.3)]"
                >
                  Sign Up Free
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              ref={toggleRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-300 hover:text-pink-400 focus:outline-none p-2 rounded-full hover:bg-white/5 transition-colors"
            >
              <FaBars className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div ref={menuRef} className="md:hidden bg-black border-t border-white/5 py-4 absolute left-0 w-full shadow-2xl rounded-b-2xl">
            <div className="flex flex-col space-y-4 px-6">
              <a href="#features" className="text-white hover:text-pink-400 transition-colors duration-200 font-medium py-2 border-b border-white/5">
                Features
              </a>
              <a href="#success" className="text-white hover:text-pink-400 transition-colors duration-200 font-medium py-2 border-b border-white/5">
                Success Stories
              </a>
              
              {user ? (
                /* Mobile User Profile Section */
                <div className="flex flex-col space-y-3 pt-4">
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700">
                    <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white font-semibold shadow-md">
                      {user?.profilePhoto ? (
                        <img 
                          src={buildMediaUrl(user.profilePhoto)} 
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-semibold text-lg">
                          {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{user?.membershipType === 'premium' ? 'Premium Member' : 'Free Member'}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2 mt-2">
                    <button className="px-4 py-2.5 text-slate-700 dark:text-slate-300 hover:text-pink-600 dark:hover:text-pink-400 rounded-lg font-medium hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-200 text-left">
                      View Profile
                    </button>
                    <button className="px-4 py-2.5 text-slate-700 dark:text-slate-300 hover:text-pink-600 dark:hover:text-pink-400 rounded-lg font-medium hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-200 text-left">
                      Settings
                    </button>
                    <button 
                      onClick={logout}
                      className="px-4 py-2.5 text-rose-600 dark:text-rose-400 rounded-lg font-medium hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all duration-200 text-left mt-2"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                /* Mobile Login/Signup Buttons */
                <div className="flex flex-col space-y-3 pt-4">

                  <button 
                    onClick={() => openLogin()}
                    className="w-full py-3 text-pink-400 border-2 border-pink-500/20 rounded-xl font-bold hover:bg-white/5 transition-all duration-200"
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => openSignup()}
                    className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold hover:shadow-[0_8px_20px_rgba(236,72,153,0.3)] transition-all duration-200"
                  >
                    Sign Up Free
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;